import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { Navigate } from "react-router-dom";
import {
  AuthError,
  type AuthUser,
  getAccessToken,
  initialize as authInitialize,
  isEntra,
  login as authLogin,
  loginInteractive as authLoginInteractive,
  logout as authLogout,
  refreshIfNeeded,
  restoreSession,
} from "@/lib/auth";

interface AuthContextType {
  user: AuthUser | null;
  accessToken: string | null;
  authenticated: boolean;
  loading: boolean;
  /** true si el JWT/cuenta contiene el rol 'admin'. */
  isAdmin: boolean;
  /** Devuelve la AuthError si las credenciales son malas, o null en éxito (keycloak). */
  login: (username: string, password: string) => Promise<AuthError | null>;
  /** Login interactivo externo (entra: redirección a Microsoft). */
  loginInteractive: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  accessToken: null,
  authenticated: false,
  loading: true,
  isAdmin: false,
  login: async () => null,
  loginInteractive: async () => {},
  logout: async () => {},
});

// Polling de refresh: mejor que `setTimeout(expiresIn)` porque sobrevive
// a pestañas pausadas y relojes desincronizados. Solo aplica a keycloak;
// en entra `acquireTokenSilent` ya renueva bajo demanda en cada request.
const REFRESH_POLL_MS = 30_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const initRef = useRef(false);

  // Inicializar el proveedor y restaurar sesión al montar.
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    (async () => {
      // En entra esto ejecuta MSAL.initialize() + handleRedirectPromise();
      // en keycloak es un no-op.
      await authInitialize();
      const restored = await restoreSession();
      if (restored) {
        setUser(restored);
        setAccessToken(getAccessToken());
      }
      setLoading(false);
    })();
  }, []);

  // Refresh proactivo: solo keycloak lo necesita.
  useEffect(() => {
    if (!user || isEntra) return;
    const interval = window.setInterval(async () => {
      const token = await refreshIfNeeded();
      if (!token) {
        // Refresh caducado: sesión muerta.
        setUser(null);
        setAccessToken(null);
        return;
      }
      setAccessToken(token);
    }, REFRESH_POLL_MS);
    return () => window.clearInterval(interval);
  }, [user]);

  const login = useCallback(async (username: string, password: string) => {
    try {
      const loggedUser = await authLogin(username, password);
      setUser(loggedUser);
      setAccessToken(getAccessToken());
      return null;
    } catch (err) {
      if (err instanceof AuthError) return err;
      return new AuthError(0, "Error de red al iniciar sesión");
    }
  }, []);

  const loginInteractive = useCallback(async () => {
    // Redirige a Microsoft; al volver, el flujo de montaje recupera la sesión.
    await authLoginInteractive();
  }, []);

  const logout = useCallback(async () => {
    await authLogout();
    setUser(null);
    setAccessToken(null);
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-100">
        <div className="text-center space-y-3">
          <div className="h-8 w-8 mx-auto animate-spin rounded-full border-4 border-brand border-t-transparent" />
          <p className="text-sm text-content-muted">Conectando…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        authenticated: true,
        loading,
        isAdmin: user.roles.includes("admin"),
        login,
        loginInteractive,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
